<template>
  <div
    class="inputContainer relative w-full"
    :class="[
      {
        containsError: firstChangeDone && (inputValidation || inputValidation),
        isFocus,
        withPrefix: !!prefix,
        withSuffix: !!suffix,
        alwaysShowPrefix: !!alwaysShowPrefix,
        alwaysShowSuffix: !!alwaysShowSuffix,
        alwaysShowPasswordEye: !!alwaysShowSuffix,
        empty: Commons.isEmpty(inputText),
      },
      `type-${type}`,
    ]"
    ref="inputContainer"
  >
    <div class="relative w-full h-full innerInputContainer overflow-hidden">
      <div
        class="absolute prefix h-full left-0 flex items-center justify-center px-4 opacity-0 pointer-events-none"
        :class="{ 'opacity-100': alwaysShowPrefix || isFocus || inputText }"
        v-if="!!prefix"
      >
        <slot name="prefix">
          <ImgComponent
            class="h-full"
            :src="prefix"
            v-if="Commons.isImgString(prefix)"
          ></ImgComponent>
          <span v-else v-html="prefix"></span>
        </slot>
      </div>
      <div
        class="absolute suffix h-full right-0 flex items-center justify-center px-4 opacity-0 pointer-events-none"
        :class="{ 'opacity-100': alwaysShowSuffix || isFocus || inputText }"
        v-if="!!suffix"
      >
        <slot name="suffix">
          <ImgComponent
            class="h-full"
            :src="suffix"
            v-if="Commons.isImgString(suffix)"
          ></ImgComponent>
          <span v-else v-html="suffix"></span>
        </slot>
      </div>

      <div
        class="absolute passwordEye top-1/2 -translate-y-1/2 right-4 opacity-0 flex items-center justify-end"
        :class="{
          'opacity-100': alwaysShowPasswordEye || isFocus || inputText,
        }"
        v-if="type == 'password'"
        @click="showPassword = !showPassword"
      >
        <img
          src="./assets/images/eye.png"
          class="w-6 h-6 opacity-50 onlyLightTheme"
          alt=""
        />
        <img
          src="./assets/images/eye-white.svg"
          class="w-6 h-6 opacity-50 onlyDarkTheme"
          alt=""
        />
      </div>
      <input
        v-bind="{
          ...inputProperties,
          ...(type == 'number' ? { inputmode: 'numeric' } : {}),
        }"
        :required="required"
        :disabled="disabled"
        v-model="inputText"
        :type="
          type === 'password' && showPassword
            ? 'text'
            : type === 'number' && allowTrailingZero
              ? 'text'
              : type
        "
        :class="{
          empty: Commons.isEmpty(inputText),
          'hidden opacity-0 pointer-events-none w-full': type === 'textarea',
        }"
        class="h-full w-full"
        :placeholder="placeHolder || ' '"
        :step="numberStep"
        ref="input"
        @focus="
          () => {
            $emit('focus')
            isFocus = true
          }
        "
        @blur="
          () => {
            $emit('blur')
            isFocus = false
          }
        "
        @keydown="
          e => {
            if (preventKeyDown) e.preventDefault()
          }
        "
        @input.stop="() => {}"
      />
      <textarea
        :required="required"
        class="!h-full overflow-auto w-full"
        :class="{
          empty: Commons.isEmpty(inputText),
        }"
        :placeholder="placeHolder || ' '"
        v-if="type === 'textarea'"
        v-model="inputText"
        @focus="isFocus = true"
        @blur="isFocus = false"
        @input.stop="() => {}"
      >
      </textarea>
    </div>

    <label
      class="absolute"
      :class="{ placeholderExists: !!placeHolder, isDate: type == 'date' }"
      v-if="loaded && label"
    >
      <div
        class="truncate"
        :style="{
          maxWidth: ($refs.inputContainer?.offsetWidth || 100) - 10 + 'px',
        }"
      >
        <slot name="label">
          <span v-html="`${label}${required ? '' : ''}`"></span>
        </slot>
      </div>
    </label>

    <span
      v-if="loaded && firstChangeDone && inputValidation"
      class="errorContainer absolute"
    >
      <div
        :style="{
          maxWidth: ($refs.inputContainer?.offsetWidth || 100) - 10 + 'px',
        }"
      >
        {{ inputValidation }}
      </div>
    </span>
    <span v-else-if="detailText" class="detailContainer absolute">
      <div
        :style="{
          maxWidth: ($refs.inputContainer?.offsetWidth || 100) - 10 + 'px',
        }"
      >
        <slot name="detailText">
          <span v-html="detailText"></span>
        </slot>
      </div>
    </span>
  </div>
</template>

<script>
import { defineComponent } from 'vue'
import Commons from '@/shared/utils/commons'
import { debounce } from 'lodash-es'
import ImgComponent from '../img/ImgComponent.vue'

export default defineComponent({
  name: 'TextInput',
  components: { ImgComponent },
  props: {
    value: {
      type: [String, Number, Boolean],
      default: '',
    },
    modelValue: {
      type: [String, Number, Boolean],
      default: '',
    },
    label: {
      type: String,
      default: '',
    },
    detailText: {
      type: String,
      default: '',
    },
    type: {
      type: String,
      default: '',
    },
    required: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    allowTrailingZero: {
      type: Boolean,
      default: false,
    },
    placeHolder: {
      type: String,
      default: '',
    },
    prefix: {
      type: String,
      default: '',
    },
    suffix: {
      type: String,
      default: '',
    },
    alwaysShowPrefix: {
      type: Boolean,
      default: false,
    },
    alwaysShowSuffix: {
      type: Boolean,
      default: false,
    },
    alwaysShowPasswordEye: {
      type: Boolean,
      default: false,
    },
    forceUppercase: {
      type: Boolean,
      default: false,
    },
    trim: {
      type: Boolean,
      default: false,
    },
    preventKeyDown: {
      type: Boolean,
      default: false,
    },
    min: {
      type: [Number, String],
      default: 0,
    },
    minLenght: {
      type: [Number, String],
      default: 0,
    },
    max: {
      type: [Number, String],
      default: '',
    },
    numberStep: {
      type: [Number, String],
      default: 1,
    },
    validate: {
      type: Function,
      default: () => {
        return ''
      },
    },
    inputProperties: {
      type: Object,
      default: () => {
        return {}
      },
    },
  },
  setup() {
    return {
      Commons,
    }
  },
  data() {
    return {
      showPassword: false,
      inputText: '',
      firstChangeDone: false,
      watchFirstChange: false,
      loaded: false,
      isFocus: false,
      inputValidation: '',
    }
  },
  mounted() {
    this.initDebunceValidation()
    this.reloadInputContent()
    setTimeout(() => {
      this.watchFirstChange = true
    }, 2000)
    this.loaded = true
  },
  methods: {
    initDebunceValidation() {
      const vue = this
      this.makeInputValidation = debounce(function () {
        vue.makeInputValidationDebunced()
      }, 100)
    },
    makeInputValidation() {},
    makeInputValidationDebunced() {
      const validationReponse = this.getInputValidation()
      this.$refs.input?.setCustomValidity(validationReponse)
      this.inputValidation = validationReponse
      this.$forceUpdate()
    },
    reloadInputContent() {
      this.inputText = this.modelValue || this.value
      // this.makeInputValidation();
    },
    getInputValidation(newValue = this.inputText) {
      if (this.disabled) return ''

      let error = this.validate(newValue)
      error ||= this.validateRequired(newValue, this.type, this.required)
      error ||= this.validateType(newValue, this.type)
      error ||= this.validateMin(newValue, this.type, this.min)
      error ||= this.validateMax(newValue, this.type, this.max)
      error ||= this.validateMinLenght(newValue, this.minLenght)

      return error || ''
    },
    validateType(value, inputType) {
      if (inputType == 'email') {
        if (/^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/.test(value))
          return ''
        return 'Email incorrect correct'
      }
      if (inputType == 'number') {
        if (isNaN(parseFloat(value))) return 'Valeur incorrect'
        if (isFinite(value)) return ''
        return 'Valeur incorrect'
      }
      if (inputType == 'toggle') {
        if (typeof value == 'boolean') return ''
        return 'Valeur incorrect'
      }
      return ''
    },
    validateRequired(value, inputType, required) {
      if (!required) return ''
      if (inputType == 'number' && value == 0) return ''
      if (inputType == 'toggle') return ''
      if (!value) return 'Ce champ est requis'
    },
    validateMin(value, inputType, min) {
      if (inputType !== 'number') return ''
      if (value >= min) return ''
      return 'Le minimum est ' + min
    },
    validateMax(value, inputType, max) {
      if (inputType !== 'number') return ''
      if (!max) return ''
      if (value <= max) return ''
      return 'Le maximum est ' + max
    },
    validateMinLenght(value, minLenght) {
      if (`${value}`.length >= minLenght) return ''
      return 'Cette valeur est trop courte '
    },
    externalSetValue(value) {
      this.inputText = value
    },
  },
  watch: {
    value(newValue) {
      this.inputText = newValue
      if (this.watchFirstChange) this.firstChangeDone = true
      // this.makeInputValidation();
    },
    modelValue(newValue) {
      this.inputText = newValue
      if (this.watchFirstChange) this.firstChangeDone = true
    },
    inputText(newValue, old) {
      const isTextType = this.type == 'text' || this.type == 'textarea'
      if (
        this.forceUppercase &&
        isTextType &&
        `${newValue}`.toUpperCase() != newValue
      ) {
        this.inputText = `${newValue}`.toUpperCase()
        this.$forceUpdate()
        return
      }
      if (this.trim && isTextType && `${newValue}`.trim() != newValue) {
        console.log('will trim')
        this.inputText = `${newValue}`.trim()
        this.$forceUpdate()
        return
      }
      if (this.type == 'number' && newValue !== '') {
        const intValue = parseFloat(newValue)
        if (!/^-?\d*\.?\d*$/.test(newValue)) {
          this.inputText = old || 0
          this.$forceUpdate()
          return
        }
        if (intValue !== newValue && !this.allowTrailingZero) {
          this.inputText = intValue || 0
          this.$forceUpdate()
          return
        }
        if (this.inputProperties?.maxlength) {
          const size = `${intValue}`.length
          if (size > this.inputProperties?.maxlength) {
            this.inputText = old
            this.$forceUpdate()
            return
          }
        }
        if (this.max) {
          if (intValue > this.max) {
            this.inputText = this.max
            this.$forceUpdate()
            return
          }
        }
        if (/^0{2,}/i.test(`${newValue}`)) {
          this.inputText = old || 0
          this.$forceUpdate()
          return
        }
      }
      this.$forceUpdate()
      this.$emit('input', newValue)
      this.$emit('update:modelValue', newValue)
      this.$emit('input:modelValue', newValue)
      const vue = this
      this.$emit('valueWillChange', {
        newValue,
        reset(resetValue = old) {
          vue.inputText = resetValue
        },
      })

      this.makeInputValidation()
      if (this.watchFirstChange) this.firstChangeDone = true
      this.$forceUpdate()
    },
    inputValidation(newVal) {
      this.$refs?.input?.setCustomValidity(newVal)
      this.$forceUpdate()
    },
  },
  computed: {
    // inputValidation() {
    //   return this.getInputValidation();
    // },
  },
})
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
@import url(./assets/css/input.css);
</style>
